import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AppConfig } from '@project_app/config/app.config';
import { environment } from '@environment';

import { CartApiResponse } from '@shared/models/api-response/cart.api-response';

import { MenuService } from '@core/services/menu/menu.service';
import { ToastrService } from '@core/services/notification/toastr.service';
import { CartApiService } from '@core/http/cart-api.service';
import { SessionStorageService } from '@core/services/util/session-storage.service';
import { LoginService } from '@core/services/login/login.service';
import { StorageService } from '@core/services/util/storage.service';
import { LocalStorageService } from '@core/services/util/local-storage.service';
import { PriceService } from '@core/services/price/price.service';
import { PassService } from '@core/services/pass/pass.service';
import { MembershipService } from '@core/services/membership/membership.service';
import { ImageUrlCreatorService } from '@core/services/imageUrlCreator/image-url-creator.service';
import { CustomizationService } from '@core/services/customization/customization.service';
import { PreorderService } from '@core/services/preorder/preorder.service';

import { InfoDialogComponent } from '@app/shared/components/info-dialog/info-dialog.component';
import { OldItemSelection } from '@shared/components/customization/customization.component';

import { PricableType } from '@app/shared/enums/menu.enum';
import OutletKeyConverter from '@core/services/outlet/outlet-key-converter';
import { GtmService } from '@core/services/analytics/gtm.service';
import { CartItem } from '@app/shared/models/cart-item';
import { SentryBreadcrumbsService } from '../sentry-breadcrumbs.service';

export interface Item {
    brand_name: string;
    combo_bogo_remarks: {};
    cost: number;
    discounted_cost: number;
    type: string;
    brand_id: number;
    ids: any;
    is_subscription_item: boolean;
    id: string;
    product_id: number;
    quantity: number;
    remark: string;
    price: number;
    marked_price: number;
}

export class LocalCart {
    items: Item[];
    itemDetails: any;
    constructor() {}
}

@Injectable({
    providedIn: 'root'
})
export class CartService {
    menuModel: any;

    state = new BehaviorSubject<LocalCart>(new LocalCart()); // browser memory cart
    serverCart = new BehaviorSubject<any>({}); // server cart
    updateCurrencySelection = new BehaviorSubject<any>(false);
    crossBrandReset = new BehaviorSubject<any>({});
    isCurrencyUsed = false;

    isCartChanged = new BehaviorSubject<boolean>(false);
    isCartShaking = new BehaviorSubject<boolean>(false);
    freebiePopulated = new BehaviorSubject<boolean>(false);

    constructor(
        private menuService: MenuService,
        private toastrService: ToastrService,
        private storageService: StorageService,
        private cartApiService: CartApiService,
        private sessionStorage: SessionStorageService,
        private loginService: LoginService,
        private router: Router,
        private localStorage: LocalStorageService,
        private customizationService: CustomizationService,
        private priceService: PriceService,
        private passService: PassService,
        private membershipService: MembershipService,
        private imageUrlCreator: ImageUrlCreatorService,
        private preorderService: PreorderService,
        private dialog: MatDialog,
        private gtmService: GtmService,
        private sentryBreadcrumbsService: SentryBreadcrumbsService
    ) {
        const cart = this._getCart();
        this.state.next(cart);

        this.menuService.menuModel.subscribe((menuModel: any) => {
            this.menuModel = menuModel;
        });

        this.state.subscribe(() => {
            this.priceService.calculateMenuPrices();
        });
    }

    private _getCart() {
        this._maintainCartVersioning();
        let cart = this.localStorage.get('cart');

        if (!cart) {
            cart = Object.create(null, { items: { value: Object.create(null), enumerable: true } });
            this.localStorage.set('cart', cart);
        }
        return cart;
    }

    private _maintainCartVersioning() {
        // change AppConfig.cart_version whenever local cart schema changes
        const cartVersion = this.localStorage.get('cart_version');

        if (!cartVersion || cartVersion !== AppConfig.cart_version) {
            this.localStorage.set('cart_version', AppConfig.cart_version);
            this.resetCart();
        }
    }

    private _getMenuData(is_new_product: boolean, menu_id?: number) {
        if (is_new_product) {
            return this.menuService.menuModel.value;
        }

        const cart = this.localStorage.get('cart');
        return cart.itemDetails[menu_id];
    }

    getProductQuantity(productId: number): number {
        const cart = this.state.value;
        return Object.values(cart.items).reduce((acc: number, item: any) => {
            const { ids, quantity } = item;
            const { menu_id, product_id } = ids;
            if (menu_id === this.menuModel.menu_id && product_id === productId) {
                return acc + quantity;
            }
            return acc;
        }, 0);
    }

    getProductQuantities(items: any): Record<number, number> {
        const currentMenuId = this.menuModel.menu_id;

        return Object.values(items).reduce<Record<number, number>>(
            (acc, item: any) => {
                const { ids, quantity } = item;
                const { menu_id, product_id } = ids;

                if (menu_id === currentMenuId) {
                    acc[product_id] = (acc[product_id] || 0) + quantity;
                }

                return acc;
            },
            {} as Record<number, number>
        );
    }

    getQuantity(): number {
        const cart = this.state.value;
        return Object.values(cart.items).reduce((acc: number, item: any) => acc + item.quantity, 0);
    }

    getItemId(sizeId: number, customizationString: string = null): string {
        const menuData = this._getMenuData(true);
        const { outlet_id, menu_id } = menuData;
        return [outlet_id, menu_id, sizeId, customizationString].filter(Boolean).join('-');
    }

    // removes all the pass from cart if exist
    removeExistingPassFromCart() {
        const cart = this.state.value;
        Object.values(cart.items).forEach(item => {
            if (item.is_subscription_item) {
                this.decrementQuantity(item);
            }
        });
    }

    getProductSizeDescription(productSize) {
        return productSize?.description || productSize?.label;
    }

    getCustomizationsLabelString(customizationIds, menuData, product?, productSize?) {
        if (!customizationIds) {
            return '';
        }

        const customizationIdArray = customizationIds.split('-');
        const customizations = this.customizationService.getCustomizationsObject(customizationIdArray, menuData);

        if (product && productSize && product.grouped_ct) {
            return this.getComboDescription(customizations, menuData, productSize, product);
        }
        return customizations.reduce((acc, item) => [acc, item.label].filter(Boolean).join(', '), '');
    }

    getComboDescription(customizations, menuData, productSize, product) {
        const desObj = {};
        for (const custType of productSize.product_customization_types) {
            const customizationList = customizations.filter(val => {
                return val.product_customization_type_id == custType.id;
            });
            for (const index in customizationList)
                if (desObj && desObj[custType.ct_group_id]) {
                    desObj[custType.ct_group_id]['label'].push(customizationList[index]['label']);
                    desObj[custType.ct_group_id]['id'].push(customizationList[index]['id']);
                    const isVeg = this.isItemVeg(product, desObj[custType.ct_group_id]['id'].join('-'), menuData);
                    desObj[custType.ct_group_id]['veg'] = isVeg;
                } else {
                    desObj[custType.ct_group_id] = {
                        veg: customizationList[index]['veg'],
                        label: [customizationList[index]['label']],
                        id: [customizationList[index]['id']]
                    };
                }
        }
        return desObj;
    }

    getProductDescription(productSize, customizations, menuData, product?) {
        const { label } = productSize;

        if (product && product.grouped_ct) {
            customizations = this.getCustomizationsLabelString(customizations, menuData, product, productSize);
            return customizations;
        }

        customizations = this.getCustomizationsLabelString(customizations, menuData);
        return customizations.length > 0 ? label + ' ' + customizations : label;
    }

    isItemVeg(product, customizationIds, menuData) {
        const customizationIdArray = customizationIds.split('-');
        const customizations = this.customizationService.getCustomizationsObject(customizationIdArray, menuData);
        return customizations.reduce((acc, item) => acc && item.veg, product.veg);
    }

    getCartItemMarkedPrice(menuData, productSizeId, customizationString, quantity = 0) {
        return this.priceService.getCartItemMarkedPrice(menuData, productSizeId, customizationString) * quantity;
    }

    getCartItemPrice(menuData, productSizeId, customizationString, quantity = 0) {
        return this.priceService.getCartItemPrice(menuData, productSizeId, customizationString) * quantity;
    }

    _addMoreInfo(ids: any, menuData: any) {
        const product = menuData.products[ids.product_id];
        const productSize = menuData.product_sizes[ids.product_size_id];

        const payload = {
            ids,
            label: this.getProductSizeDescription(productSize),
            description: this.getProductDescription(productSize, ids.customization_ids, menuData, product),
            is_subscription_item: product.is_subscription_item,
            is_customizable: this.customizationService.toShowCustomizationModal(product.id, menuData),
            is_veg: this.isItemVeg(product, ids.customization_ids, menuData),
            brand_id: menuData.brand_id || environment.brand.id,
            brand_name: menuData.brand_name || environment.brand.name
        };

        return payload;
    }

    private _modifyItemDetails(menuData: any, cart: any, productId: number) {
        const itemDetails = cart.itemDetails[menuData.menu_id] || Object.create(null);

        // ensure key present in object
        const menuProperties = [
            'products',
            'product_sizes',
            'product_categories',
            'product_customization_types',
            'product_customizations',
            'ct_groups',
            'product_t_config',
            'max_total_quantity'
        ];
        menuProperties.forEach(key => {
            if (!itemDetails[key]) {
                itemDetails[key] = Object.create(null);
            }
        });

        const product = menuData.products[productId];
        itemDetails.products[product.id] = product;
        itemDetails.brand_id = menuData.brand_id;
        itemDetails.brand_name = menuData.brand_name;
        itemDetails.product_categories[product.category_id] = menuData.product_categories[product.category_id];
        itemDetails.ct_groups = menuData.ct_groups;
        itemDetails.product_t_config = menuData.product_t_config;
        itemDetails.max_total_quantity = menuData.max_total_quantity;

        product.product_sizes.forEach((size: any) => {
            itemDetails.product_sizes[size.id] = size;
            size.product_customization_types.forEach((pct: any) => {
                itemDetails.product_customization_types[pct.id] = menuData.product_customization_types[pct.id];
                Object.values(menuData.product_customizations).forEach((customization: any) => {
                    if (customization.product_customization_type_id === pct.id) {
                        itemDetails.product_customizations[customization.id] = customization;
                    }
                });
            });
        });

        return itemDetails;
    }

    isAlreadyInCart(cart: any, cartItemId: string) {
        if (!cart) {
            return false;
        }
        return cart.items[cartItemId] ? true : false;
    }

    private _updateCartItemsPrice(cart: any) {
        if (this.router.url === '/cart') {
            const originalCart = this.localStorage.get('cart');
            this.updateOutletTiming(originalCart, cart);
        }

        this.passService.setCartPass(cart);

        Object.values(cart.items).forEach((item: any) => {
            if (!item.is_subscription_item) {
                const cartMenuData = cart.itemDetails[item.ids.menu_id];
                const { price, marked_price } = this._calculatePrice(
                    cartMenuData,
                    item.ids.product_size_id,
                    item.ids.customization_ids
                );
                item.price = price;
                item.marked_price = marked_price;
            }
        });

        this.hydrateState(cart);
    }

    updatePrices() {
        const cart = this._getCart();
        this._updateCartItemsPrice(cart);
    }

    updateOutletTiming(originalCart, updatedCart) {
        if (originalCart.items && updatedCart.items && Object.keys(updatedCart.items).length) {
            const brandIds = this.getBrandIds(Object.values(originalCart.items));
            const updatedBrandIds = this.getBrandIds(Object.values(updatedCart.items));
            if (JSON.stringify(brandIds) !== JSON.stringify(updatedBrandIds)) {
                this.preorderService.getPreorderSlots(updatedBrandIds);
            }
        }
    }

    getBrandIds(cartItems?): number[] {
        if (!cartItems) {
            const cart = this._getCart();
            cartItems = Object.values(cart.items);
        }
        return [...new Set(cartItems.map(cartItem => cartItem.brand_id))] as number[];
    }

    isCustomizingPrevSelection(oldSelection: any) {
        return !oldSelection || !Object.keys(oldSelection).length ? false : true;
    }

    addItem(
        productId: number,
        sizeId: number,
        customizations: string,
        payload: { quantity?: number; remark?: string; oldSelection?: OldItemSelection; menuData?: any } = {}
    ) {
        // max quantity handling .. product and cart
        const cart = this.state.value;
        const { quantity, remark, oldSelection } = payload;
        let { menuData } = payload;

        if (!menuData) {
            const is_customizing_prev_selection = this.isCustomizingPrevSelection(oldSelection);
            menuData = oldSelection
                ? this._getMenuData(!is_customizing_prev_selection, oldSelection.menu_id)
                : this._getMenuData(true);
        }

        const { outlet_id, menu_id } = oldSelection ? oldSelection : menuData;

        const itemId = [outlet_id, menu_id, sizeId, customizations].filter(Boolean).join('-');
        const product = menuData.products[productId];

        const Ids = {
            product_id: productId,
            product_size_id: sizeId,
            customization_ids: customizations,
            outlet_id: outlet_id,
            menu_id: menu_id
        };

        // if current item to be added is pass, remove all the passes from cart
        if (product?.is_subscription_item) {
            this.removeExistingPassFromCart();
        }

        if (oldSelection) {
            const oldItemId = [outlet_id, menu_id, oldSelection.product_size_id, oldSelection.customizations]
                .filter(Boolean)
                .join('-');

            if (oldItemId === itemId) {
                if (cart.items[itemId].quantity === quantity) {
                    return true;
                }
                cart.items[itemId].quantity = quantity;
            } else {
                delete cart.items[oldItemId];
                if (this.isAlreadyInCart(cart, itemId)) {
                    cart.items[itemId].quantity += quantity || 1;
                } else {
                    cart.items[itemId] = new CartItem(itemId, quantity, remark, this._addMoreInfo(Ids, menuData));
                }
            }
        } else {
            if (this.isAlreadyInCart(cart, itemId)) {
                cart.items[itemId].quantity += quantity || 1;
            } else {
                cart.items[itemId] = new CartItem(itemId, quantity || 1, remark, this._addMoreInfo(Ids, menuData));
            }
        }

        cart.itemDetails = cart.itemDetails || {};
        cart.itemDetails[menuData.menu_id] = this._modifyItemDetails(menuData, cart, productId);

        this.hydrateGtmData('add', cart.items[itemId]);
        this._updateCartItemsPrice(cart);
        return true;
    }

    checkMaxLimit(productId, menu, selectedQuantity?) {
        const cart = this.state.value;
        let totalproductQty = 0;
        const product = menu.products[productId];

        if (!product) {
            return true;
        }

        let totalCartQty = 0;

        Object.values(cart.items).forEach(item => {
            if (!item.is_subscription_item) {
                //remove subscription item from total count
                totalCartQty += item.quantity;
            }
            if (item.ids.product_id == product.id) {
                totalproductQty += item.quantity;
            }
        });

        if (selectedQuantity) {
            totalCartQty += selectedQuantity;
            totalproductQty += selectedQuantity;
        }

        if (product && product.max_qty && totalproductQty >= product.max_qty) {
            this.toastrService.openSnackBar(`Maximum quantity selection limit for this item is ${product.max_qty}`, 'OKAY');
            return false;
        }

        if (totalCartQty >= menu.max_total_quantity) {
            this.toastrService.openSnackBar(
                `Oops! You can add a maximum of ${menu.max_total_quantity} items to your cart.`,
                'OKAY'
            );
            return false;
        }

        return true;
    }

    _calculatePrice(menuData: any, sizeId: number, customizations: string) {
        const price = this.priceService.getCartItemPrice(menuData, sizeId, customizations);
        const marked_price = this.priceService.getCartItemMarkedPrice(menuData, sizeId, customizations);
        return { price, marked_price };
    }

    incrementQuantity(item: any) {
        const cart = this.state.value;
        cart.items[item.id].quantity += 1;
        this._updateCartItemsPrice(cart);
        this.hydrateGtmData('add', item);
    }

    decrementQuantity(item: any) {
        const cart = this.state.value;
        if (!cart.items[item.id]) {
            return;
        }
        if (item.quantity === 1) {
            delete cart.items[item.id];
        } else {
            cart.items[item.id].quantity -= 1;
        }
        this._updateCartItemsPrice(cart);
        this.hydrateGtmData('remove', item);
    }

    decrementMultipleItems(items: Item[]) {
        const cart = this.state.value;

        items.forEach((item: Item) => {
            if (!cart.items[item.id]) {
                return;
            }

            delete cart.items[item.id];
            this.hydrateGtmData('remove', item);
        });

        this._updateCartItemsPrice(cart);
    }

    hydrateGtmData(operation, item) {
        if (item.is_subscription_item) {
            this.gtmService.logPassUpdation(operation, item);
            return;
        }

        const cart = this.state.value;
        const menuData = cart.itemDetails[item.ids.menu_id];

        if (!menuData) {
            const cartMenuId = Object.keys(cart.itemDetails).join('-');
            this.gtmService.logEvent('js_errors', {
                event_label: 'GTM log err: Undefined Menu Data',
                event_value: `${cartMenuId}: ${item.ids.menu_id}`
            });
            return;
        }

        const product = menuData.products[item.ids.product_id];
        const productSize = menuData.product_sizes[item.ids.product_size_id];
        const category = product ? menuData.product_categories[product.category_id] : '';
        const quantity = item.quantity;
        const brandName = menuData.brand_name;

        switch (operation) {
            case 'add':
                this.gtmService.addToCart(category, product, productSize, quantity, brandName);
                break;

            case 'remove':
                this.gtmService.removeFromCart(category, product, productSize, quantity, brandName);
                break;

            default:
                // code...
                break;
        }
    }

    toShowCannotRemoveAlert(productId: number, menuId: number) {
        const cart = this.state.value;
        const cartItemIds = Object.values(cart.items)
            .filter(item => {
                const { product_id, menu_id } = item.ids;
                return product_id === productId && menu_id === menuId;
            })
            .map(item => item.id);

        return cartItemIds.length > 1 ? { error: true } : { error: false, itemId: cartItemIds[0] };
    }

    removeItem(productId: number, menuId: number) {
        const { error, itemId } = this.toShowCannotRemoveAlert(productId, menuId);

        if (error) {
            this.toastrService.openSnackBar('Use cart to edit the product', 'OKAY');
            return;
        }

        const cart = this.state.value;
        this.decrementQuantity(cart.items[itemId]);
        return true;
    }

    hydrateState(cart: any) {
        this.localStorage.set('cart', cart);
        this.sessionStorage.remove('cart');
        this.state.next(cart);
        this.isCartChanged.next(true);
    }

    hydrateServerCart(serverCart: CartApiResponse['cart']) {
        this.serverCart.next(serverCart);
        this.sessionStorage.set('cart', serverCart);
        this.isCartChanged.next(false);
    }

    private _clearLocalCart() {
        const cart = Object.create(null, { items: { value: Object.create(null), enumerable: true } });
        this.state.next(cart);
        this.localStorage.set('cart', cart);
        this.isCartChanged.next(true);
    }

    private _clearServerCart() {
        // const cart = Object.create(null, { items: { value: Object.create(null), enumerable: true } });
        this.serverCart.next(null);
        this.sessionStorage.remove('cart');
    }

    private _clearLocalSamplingCart() {
        this.localStorage.set('freebie_cart', {});
    }

    resetCart() {
        this._clearLocalSamplingCart();
        this._clearLocalCart();
        this._clearServerCart();
    }

    resetServerCart() {
        this._clearServerCart();
    }

    async createCart(outletServiceType?: string) {
        const tip_amount: number = this.sessionStorage.get('tip_amount') || 0;

        await this.loginService.ensureSessionId(true);
        return new Promise(resolve => {
            lastValueFrom(this.cartApiService.create(outletServiceType, tip_amount)).then(
                (data: CartApiResponse) => {
                    if (data && data.cart && Object.keys(data.cart).length) {
                        this.handleErrorState(data);
                        this.hydrateServerCart(data.cart);
                        this.gtmService.beginCheckout(data.cart);
                    }
                    return resolve(data);
                },
                (errorResponse: any) => {
                    if (errorResponse.error && errorResponse.error.meta) {
                        this.toastrService.openSnackBar(errorResponse.error.meta.message, 'OKAY');
                    } else {
                        this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
                    }
                }
            );
        });
    }

    handleErrorState(data: CartApiResponse) {
        if (data.meta.code === 406) {
            const dialogConfig = new MatDialogConfig();
            dialogConfig.minWidth = 350;
            dialogConfig.panelClass = 'info-dialog';
            dialogConfig.data = {
                message: data.meta.message,
                buttonText: 'OKAY'
            };

            this.dialog.open(InfoDialogComponent, dialogConfig);
        }

        this.syncLocalCart(data.cart);
    }

    syncLocalCart(serverCart) {
        const serverCartItemIds = serverCart.items.map(item => item.id);

        if (serverCartItemIds.length) {
            const localCartItem = this.state.value.items;
            Object.values(localCartItem).forEach(item => {
                if (item.is_subscription_item && serverCartItemIds.indexOf(item.id) === -1) {
                    this.destroyItem(item.id);
                }
            });
        } else {
            this.resetCart();
        }
    }

    destroyItem(itemId) {
        const cart = this.state.value;
        cart.items[itemId].quantity = 1; //delete this item --> can be used to delete item with multiple quanity
        this.decrementQuantity(cart.items[itemId]);
    }

    updateCart(addressId: number, orderTime: number, zeroContactDelivery?: boolean) {
        return new Promise(resolve => {
            lastValueFrom(this.cartApiService.update(addressId, orderTime, zeroContactDelivery)).then(
                (data: CartApiResponse) => {
                    if (data && data.cart && Object.keys(data.cart).length) {
                        this.handleErrorState(data);
                        this.hydrateServerCart(data.cart);
                        this.gtmService.addShippingInfo(data.cart);
                    }
                    return resolve(data);
                },
                (errorResponse: any) => {
                    if (errorResponse.error && errorResponse.error.meta) {
                        this.toastrService.openSnackBar(errorResponse.error.meta.message, 'OKAY');
                    } else {
                        this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
                    }
                }
            );
        });
    }

    addOutletMenuData(outlet: any) {
        const outletMenuData: any = Object.create(null);

        // create Temp hash

        const menuProperties = ['product_categories', 'product_sizes', 'product_customizations', 'ct_groups', 'product_t_config'];
        const menuHash: any = Object.create(null);

        menuProperties.forEach((key: string) => {
            if (key in outlet) {
                menuHash[key] = menuHash[key] || Object.create(null);
                for (let i = 0; i < outlet[key].length; i++) {
                    menuHash[key][outlet[key][i].id] = outlet[key][i];
                }
            }
        });

        // adding productSize and customization

        const productSoldOut = {};
        outletMenuData.product_sizes = [];
        outletMenuData.product_customizations = [];

        outlet.menu_items.forEach(menu_item => {
            if (menu_item.ptype === PricableType.ProductSize) {
                const product_size = menuHash.product_sizes[menu_item.pid];
                if (product_size) {
                    if (productSoldOut[product_size.product_id] !== undefined) {
                        productSoldOut[product_size.product_id] = productSoldOut[product_size.product_id] && menu_item.sold_out;
                    } else {
                        productSoldOut[product_size.product_id] = menu_item.sold_out;
                    }

                    outletMenuData.product_sizes.push({
                        ...product_size,
                        prices: menu_item.prices,
                        price: menu_item.prices.discounted_price,
                        marked_price: menu_item.prices.price,
                        sold_out: menu_item.sold_out
                    });
                }
            } else if (menu_item.ptype === PricableType.ProductCustomization) {
                const product_customization = menuHash.product_customizations[menu_item.pid];
                if (product_customization) {
                    outletMenuData.product_customizations.push({
                        ...product_customization,
                        prices: menu_item.prices,
                        price: menu_item.prices.discounted_price,
                        marked_price: menu_item.prices.price,
                        sold_out: menu_item.sold_out
                    });
                }
            }
        });

        // get products

        outletMenuData.products = outlet.products
            .map((product: any) => {
                if (productSoldOut[product.id] !== undefined) {
                    const isSoldOut = productSoldOut[product.id];
                    const product_layout = this.membershipService.getProductLayout(outlet);
                    return {
                        ...product,
                        image: this.imageUrlCreator.getProductImage(product.id, product_layout),
                        sold_out: isSoldOut
                    };
                }
            })
            .filter(Boolean);

        return { ...outlet, ...outletMenuData };
    }

    arrayToHash(array) {
        const hash = {};
        array.forEach(entity => {
            hash[entity.id] = entity;
        });
        return hash;
    }

    async mergeGetCart(cart: any, callback?) {
        const cartParams = cart.items
            .filter(item => item.type === 'MenuItem')
            .map((item: any) => {
                return {
                    id: item.id,
                    brand_id: item.brand_id
                };
            });

        const cartData = Object.create(null, { items: { value: Object.create(null), enumerable: true } });

        await this.cartApiService
            .getProductDetails(cartParams)
            .toPromise()
            .then((data: any) => {
                data.menus &&
                    data.menus.forEach(product_detail => {
                        const outlet = new OutletKeyConverter().replaceKey(product_detail);
                        const outletData = this.addOutletMenuData(outlet);
                        const { menuModel: data } = this.menuService.generateMenuModel(outletData);
                        cartData.itemDetails = cartData.itemDetails || {};
                        cartData.itemDetails[product_detail.menu_id] = data;
                    });

                if (!cartData.itemDetails || !Object.keys(cartData.itemDetails).length) {
                    return;
                }

                cart.items.forEach((item: any) => {
                    if (item.type !== 'MenuItem') {
                        return;
                    }

                    const [outlet_id, menu_id, sizeId, ...customizations] = [...item.id.split('-')];
                    const menuData = cartData.itemDetails[menu_id];

                    if (
                        !menuData ||
                        !menuData.product_sizes ||
                        !menuData.product_sizes[sizeId] ||
                        !menuData.product_sizes[sizeId].product_id
                    ) {
                        this.sentryBreadcrumbsService.add('Product API', 'Product Info Not Found', {
                            product_size_id: sizeId,
                            customization_ids: customizations.join('-'),
                            outlet_id: outlet_id,
                            menu_id: menu_id,
                            menuData: Boolean(menuData)
                        });
                    }

                    const Ids = {
                        product_id: menuData.product_sizes[sizeId].product_id,
                        product_size_id: sizeId,
                        customization_ids: customizations.join('-'),
                        outlet_id: outlet_id,
                        menu_id: menu_id
                    };

                    cartData.items[item.id] = new CartItem(
                        item.id,
                        item.quantity,
                        item.remark,
                        this._addMoreInfo(Ids, cartData.itemDetails[item.id.split('-')[1]])
                    );
                    const { price, marked_price } = this._calculatePrice(menuData, sizeId, customizations.join('-'));
                    // add prices
                    cartData.items[item.id].price = price;
                    cartData.items[item.id].marked_price = marked_price;
                    this.hydrateState(cartData);
                });

                if (callback) {
                    callback();
                }
            })
            .catch(err => {
                console.log('getProductDetails', err);
            });
    }

    getCart() {
        if (this.router.url && this.router.url.includes('cart')) {
            return;
        }

        const brandId = this.storageService.get('brand_id') || environment.brand.id;
        const outletData = this.sessionStorage.get(`outlet_data_${brandId}`);

        const outlet = outletData ? outletData.outlet : null;
        if (!outlet || outlet.id === 1 || !outlet.active) return;

        if (this.storageService.get('unsigned_session_id')) return;

        this.cartApiService
            .getCart()
            .toPromise()
            .then((data: CartApiResponse) => {
                if (data && data.meta && data.meta.code === 200) {
                    const cartData = JSON.parse(JSON.stringify(data.cart));

                    if (cartData.items.length > 0) {
                        const localCartData = this.localStorage.get('cart');
                        if (!localCartData || !Object.keys(localCartData.items).length) {
                            this.mergeGetCart(data.cart);
                            this.hydrateServerCart(cartData);
                            this.toastrService.openSnackBar('Cart updated from another device', 'OKAY');
                        }
                    }
                }
            })
            .catch(err => console.log(err));
    }

    initServerCart(outletServiceType?: string) {
        const serverCart = this.sessionStorage.get('cart');
        if (!serverCart) {
            const localCartItem = this.state.value.items;
            if (Object.keys(localCartItem).length) {
                this.createCart(outletServiceType);
            }
        } else {
            this.hydrateServerCart(serverCart);
        }
    }

    applyCoupon(code: string, payment_method_name: string, outlet_service_type: string, address_id: number) {
        const body = {
            code,
            payment_method_name,
            outlet_service_type,
            coupon_channel_name: 'Web',
            outlet_id: this.storageService.get('outlet_id'),
            menu_id: this.storageService.get('menu_id'),
            address_id: address_id === -1 || address_id === -2 ? null : address_id
        };

        return new Promise((resolve, reject) => {
            this.cartApiService
                .applyCoupon(body)
                .then((data: any) => {
                    if (data && data.meta && data.meta.code === 200) {
                        this.hydrateServerCart(data.cart);
                    }
                    resolve(data);
                })
                .catch(err => {
                    reject(err);
                });
        });
    }

    removeCoupon() {
        return this.cartApiService.removeCoupon().pipe(
            map((data: CartApiResponse) => {
                if (data && data.meta && data.meta.code === 200) {
                    this.hydrateServerCart(data.cart);
                }
                return data;
            })
        );
    }

    addPass(pass: any) {
        const cart = this.state.value;
        const { product_id, product_size_id, outlet_id, menu_id, label, description, price, discounted_price } = pass;
        const customizations = '';
        const itemId = [outlet_id, menu_id, product_size_id, customizations].filter(Boolean).join('-');

        const Ids = {
            product_id,
            product_size_id,
            customization_ids: customizations,
            outlet_id: outlet_id,
            menu_id: menu_id
        };

        Object.values(cart.items).forEach(item => {
            if (item.is_subscription_item) {
                if (item.quantity === 1) {
                    delete cart.items[item.id];
                }
            }
        });

        const moreInfo = {
            ids: Ids,
            label,
            description,
            is_subscription_item: 1,
            is_customizable: false,
            is_veg: true,
            brand_id: environment.brand.id,
            brand_name: environment.brand.name,
            price: discounted_price,
            marked_price: price
        };

        const quanity = 1;
        cart.items[itemId] = new CartItem(itemId, quanity, '', moreInfo);
        this._updateCartItemsPrice(cart);
        this.hydrateGtmData('add', cart.items[itemId]);
        return true;
    }

    shakeTheCart() {
        this.isCartShaking.next(true);
        setTimeout(() => this.isCartShaking.next(false), 2 * 1000);
    }

    getCartPrice() {
        const cart = this.state.value;
        return Object.values(cart.items).reduce((acc, item) => acc + item.price * item.quantity, 0);
    }

    getFinalCartPrice(): number {
        // getting server cart
        const serverCart = this.serverCart.value;
        // if local cart and server cart is different, then we will show local cart prices
        if (!this.isCartChanged.value && serverCart && Object.keys(serverCart).length) {
            return this.isCurrencyUsed ? serverCart.total_payable_with_credit : serverCart.total_payable;
        }
        // get local cart prices
        return this.getCartPrice();
    }

    addTip(tip_amount) {
        return this.cartApiService.addTip(tip_amount);
    }

    removeTip() {
        return this.cartApiService.removeTip();
    }

    multiBrandOrderAllowed(productId: number, payload: any = {}, showResetDialog: boolean = true): boolean {
        // multi_brand_order_allowed is true
        const thisBrandOutletData = this.sessionStorage.get(`outlet_data_${environment.brand.id}`);
        const multiBrandOrderAllowedFlag = thisBrandOutletData && thisBrandOutletData.outlet.multi_brand_order_allowed;
        if (multiBrandOrderAllowedFlag) {
            return true;
        }

        // new item belongs to primary brand
        const currentItemBrandId = this.menuModel.brand_id;
        if (currentItemBrandId === environment.brand.id) {
            return true;
        }

        const cartItems: any = Object.values(this.state.value.items || {});
        const distinctSecondaryBrands = cartItems
            .filter((item, index, thisArray) => {
                return thisArray.indexOf(item) === index && item.brand_id !== environment.brand.id;
            })
            .map(item => {
                return { brandId: item.brand_id, brandName: item.brand_name };
            });

        // no item in cart belongs to secondary brand
        if (!distinctSecondaryBrands.length) {
            return true;
        }

        // new item belongs to same secondary brand
        if (distinctSecondaryBrands[0].brandId === currentItemBrandId) {
            return true;
        }

        // new item belongs to different secondary brand
        const currentItemBrand = this.menuModel.brand_name;
        const preSelectedBrand = distinctSecondaryBrands[0].brandName;
        const data = { productId, payload, currentItemBrandId, currentItemBrand, preSelectedBrand };
        if (showResetDialog) {
            this.crossBrandReset.next(data);
        }

        return false;
    }

    allow_coupon() {
        const utm_campaign = this.sessionStorage.get('utm_campaign');
        const coupon_not_allowed_in_utm_campaign = ['youtube_campaign'];
        return coupon_not_allowed_in_utm_campaign.indexOf(utm_campaign) === -1;
    }

    getCartItemBrands() {
        const cartItems = Object.values(this.state.value.items);
        const distinctSecondaryBrands = cartItems
            .map(item => item.brand_id)
            .filter((itemBrandId, index, thisArray) => {
                return thisArray.indexOf(itemBrandId) === index && itemBrandId !== environment.brand.id;
            });
        return distinctSecondaryBrands;
    }

    async repeatOrder(order) {
        this.storageService.delete('address_id');

        const cartData: any = {};
        cartData.items = order.cart_items.map((item: any) => {
            const [brand_id, quantity, ...itemId] = item.split('-');
            return {
                id: itemId.join('-'),
                quantity: Number(quantity),
                remark: '',
                brand_id: brand_id,
                type: 'MenuItem'
            };
        });

        await this.mergeGetCart(cartData, () => this.router.navigateByUrl('/cart'));
    }

    submitCart() {
        return new Promise(resolve => {
            this.createCart()
                .then(() => {
                    this.router.navigateByUrl('/cart');
                })
                .finally(() => {
                    return resolve(true);
                });
        });
    }

    getProductAvailability(selectedServiceType, isDistinctMenu, outletId) {
        const serviceTypeString = (function () {
            switch (selectedServiceType) {
                case 'DIN101':
                    return 'Dine-In';
                case 'TAK101':
                    return 'Take-Away';
                default:
                    return 'Delivery';
            }
        })();

        const message = `Changing to ${serviceTypeString.toLowerCase()} will reset your cart. Would you like to continue?`;
        const storedServiceType = this.storageService.get('service_type');
        if (selectedServiceType !== storedServiceType && isDistinctMenu) {
            const dialogConfig = new MatDialogConfig();
            dialogConfig.minWidth = 350;
            dialogConfig.panelClass = 'info-dialog';
            dialogConfig.data = {
                header: 'Cart will be reset',
                message: message,
                customClass: 'info-dialog-service-type',
                buttonText: `Yes, Switch To ${serviceTypeString}`
            };

            return new Promise(resolve => {
                this.dialog
                    .open(InfoDialogComponent, dialogConfig)
                    .afterClosed()
                    .subscribe(data => {
                        if (data === 'proceed') {
                            this.gtmService.logWebAnalyticsEvent(
                                'service_type_change',
                                'Changed to ' + selectedServiceType + ' from ' + storedServiceType,
                                'outlet id:' + outletId
                            );
                            return resolve('PROCEED');
                        } else {
                            return resolve('CANCEL');
                        }
                    });
            });
        } else {
            return Promise.resolve('OKAY');
        }
    }
}
